import React, { useEffect, useRef, useState, useContext } from 'react';
import { createChart } from 'lightweight-charts';
import { emitChangeOHLCVTimeframe } from 'api/socketService';
import { transformHistoricalData, transformLiveData } from 'utils/transformData';
import SocketContext from 'utils/hooks/useSocket';
import { formatPrice } from "utils";

const TradingView = ({ activeTime, chartType}: { activeTime: string; chartType: string }) => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const seriesRef = useRef<any>(null);
  const seriesPriceLineRef = useRef<any>(null);
  const { selectedOHLCVValue, selectedCoinValue, selectedCoin } = useContext(SocketContext);
  const [price, setPrice] = useState<number | null>(null);
  const [coinOHLCV, setCoinOHLCV] = useState<any | null>(null);
  const [currentPair, setCurrentPair] = useState('');

  const currentPairName = selectedCoin && (selectedCoin?.name || selectedCoin);

  useEffect(() => {
    if (!selectedCoinValue || typeof (selectedCoinValue as any).last !== 'number') {
      return;
    }
    const nextPrice = (selectedCoinValue as any).last as number;
    if (!Number.isNaN(nextPrice) && nextPrice !== price) {
      setPrice(nextPrice);
    }
  }, [selectedCoinValue, price]);

  useEffect(() => {
    if (!selectedOHLCVValue) {
      return;
    }
    setCoinOHLCV(selectedOHLCVValue);
  }, [selectedOHLCVValue]);

  useEffect(() => {
    if (!currentPairName || currentPairName === currentPair) {
      return;
    }
    setCurrentPair(currentPairName as string);
  }, [currentPairName, currentPair]);

  let myPriceFormatter: (p: number) => string;

  myPriceFormatter = (p: number) => formatPrice(currentPairName, p);

  // TEAM_004: Create series once per chart type; keep OHLCV data and price line separate
  const createSeries = (chart: any, chartType: string) => {
    if (!chart || !chartType) {
      console.error('Chart or chartType is undefined', chart, chartType);
      return;
    }

    let series;
    switch(chartType) {
      case 'Line':
        series = chart.addLineSeries();
        break;
      case 'Bars':
        series = chart.addBarSeries();
        break;
      case 'Area':
        series = chart.addAreaSeries();
        break;
      case 'Candles':
        series = chart.addCandlestickSeries();
        break;
      default:
          console.error('Invalid chartType', chartType);
          return;
    }

    series.applyOptions({
      lastValueVisible: false, // Disable automatic last price label
    });

    const safePrice = typeof price === 'number' && !Number.isNaN(price) ? price : 0;

    const priceLine = series.createPriceLine({
      price: safePrice,
      color: 'rgba(4, 111, 232, 1)',
      lineWidth: 2,
      axisLabelVisible: true,
    });

    series.priceScale().applyOptions({
      borderColor: "#515151",
    });

    seriesRef.current = series;
    seriesPriceLineRef.current = priceLine; // Store the price line reference

  };

  useEffect(() => {
    const chart = createChart(chartContainerRef.current, {
      layout: {
        textColor: '#EFEFEF',
        background: { color: 'transparent' },
      },
      timeScale: {
        borderColor: "#515151",
        timeVisible: true,
        secondsVisible: true,
        rightOffset: 15
      },
      rightPriceScale: {
        borderColor: "#515151",
      },
      grid: {
        vertLines: {
          color: "#515151",
        },
        horzLines: {
          color: "#515151",
        }
      },
      localization: {
        priceFormatter: myPriceFormatter
      },
    });

    createSeries(chart, chartType);

    const handleResize = () => {
      chart.applyOptions({
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
      });
      chart.timeScale().fitContent();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      chart.remove();
      window.removeEventListener('resize', handleResize);
    };
  }, [chartType, currentPair]);


  useEffect(() => {
    if (!currentPair || !seriesRef.current) {
      return;
    }

    // TEAM_004: Load historical OHLCV once per timeframe/pair/chart type
    emitChangeOHLCVTimeframe(
      {
        timeframe: activeTime,
        currencyPair: currentPair,
      },
      (response: any) => {
        if (!response?.success || !seriesRef.current) {
          return;
        }

        const data = transformHistoricalData(response.data, chartType);
        seriesRef.current.setData(data);

        // Sync initial price line with last known price if available
        if (
          seriesPriceLineRef.current &&
          typeof price === 'number' &&
          !Number.isNaN(price)
        ) {
          seriesPriceLineRef.current.applyOptions({
            price,
          });
        }
      },
    );
  }, [activeTime, chartType, currentPair, price]);


  // TEAM_004: Apply each live OHLCV update directly via series.update().
  // lightweight-charts will update the last bar if time matches,
  // or append a new bar if time is greater, which naturally advances the time scale.
  useEffect(() => {
    if (!coinOHLCV || !seriesRef.current) {
      return;
    }

    try {
      const liveItem = transformLiveData(coinOHLCV, chartType) as any;

      if (typeof liveItem?.time !== 'number' || Number.isNaN(liveItem.time)) {
        return;
      }

      seriesRef.current.update(liveItem);
    } catch (err) {
      // TEAM_004: Ignore malformed live OHLCV updates to keep chart stable
      console.debug('TEAM_004: live OHLCV update skipped:', err);
    }
  }, [coinOHLCV, chartType]);

  // Update the blue price line (last trade price), independent of OHLCV candles
  useEffect(() => {
    if (seriesPriceLineRef.current && typeof price === 'number' && !Number.isNaN(price)) {
      seriesPriceLineRef.current.applyOptions({
        price,
      });
    }
  }, [chartType, price]);

  return <div ref={chartContainerRef} className="GraphStyles" style={{ height: '100%', width: '100%' }} />;
};

export default TradingView;
